import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Add, Button, Chip } from '@mesh.ai/rabbit';
import { getDeerEngagementTemplateRoute } from 'Deer/constants/nonDeerRoutes';
import { setL2Visible } from 'Deer/slice/DeerNav.slice';
import { ColDef } from 'ag-grid-community';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { routes } from 'constants/routes';

import { AgGridHeader } from 'uiToolkitV1/AgGridHeader';
import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { TextHeader4, TextLabel } from 'uiToolkitV1/Typography';

import { useArchiveTemplate, useDuplicateTemplate, useGetEngagementTemplates } from './dataHooks';

export const TEMPLATES_TYPE = {
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
} as const;

export const TemplatesTab = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const toast = useToast();

  const [selectedTemplateType, setSelectedTemplateType] = useState<keyof typeof TEMPLATES_TYPE>(TEMPLATES_TYPE.ACTIVE);
  const [templateCounts, setTemplateCounts] = useState<Record<keyof typeof TEMPLATES_TYPE, string | number>>({
    [TEMPLATES_TYPE.ACTIVE]: '',
    [TEMPLATES_TYPE.ARCHIVED]: '',
  });
  const gridRef = useRef(null);

  const {
    data: engagementTemplates,
    isLoading: isEngagementTemplatesLoading,
    refetch: refetchEngagementTemplates,
  } = useGetEngagementTemplates({
    type: selectedTemplateType,
  });
  const { mutate: archiveTemplate, isLoading: isArchiveTemplateLoading } = useArchiveTemplate();
  const { mutate: duplicateTemplate, isLoading: isDuplicateTemplateLoading } = useDuplicateTemplate();

  useEffect(() => {
    if (engagementTemplates?.data !== undefined) {
      setTemplateCounts((prev) => ({
        ...prev,
        [selectedTemplateType]: engagementTemplates.data.length,
      }));
    }
  }, [engagementTemplates?.data, selectedTemplateType]);

  const onNewClick = () => {
    let routeToGo = routes.template.engagement.index.replace(':templateId', 'new');
    if (checkNewNavEnabled()) {
      routeToGo = getDeerEngagementTemplateRoute('new');
      dispatch(setL2Visible({ isL2Visible: false }));
    }
    history.push(routeToGo);
  };

  const handleOpenTemplate = (id: number) => {
    let routeToGo = routes.template.engagement.index.replace(':templateId', String(id));
    if (checkNewNavEnabled()) {
      routeToGo = getDeerEngagementTemplateRoute(String(id));
      dispatch(setL2Visible({ isL2Visible: false }));
    }
    history.push(routeToGo);
  };

  const handleArchiveTemplate = useCallback(
    (id: number, shouldArchive: boolean) => {
      archiveTemplate(
        { templateId: id, shouldArchive },
        {
          onSuccess: () => {
            if (shouldArchive) {
              toast({ status: 'success', title: 'Template archived successfully!' });
            } else {
              toast({ status: 'success', title: 'Template unarchived successfully!' });
            }
            refetchEngagementTemplates();
          },
          onError: (err: { message?: string }) => {
            toast({ status: 'error', title: err.message || 'Something went wrong while archiving the template!' });
          },
        }
      );
    },
    [archiveTemplate, toast, refetchEngagementTemplates]
  );

  const handleDuplicateTemplate = useCallback(
    (id: number) => {
      duplicateTemplate(
        { templateId: id },
        {
          onSuccess: (response) => {
            toast({ status: 'success', title: 'Template duplicated successfully!' });
            const templateRoute = checkNewNavEnabled()
              ? getDeerEngagementTemplateRoute(response.id)
              : routes.template.engagement.index.replace(':templateId', response.id);
            history.push(templateRoute);
          },
          onError: (err: { message?: string }) => {
            toast({ status: 'error', title: err.message || 'Sorry, unable to duplicate the template!' });
          },
        }
      );
    },
    [duplicateTemplate, toast, history]
  );

  const columnDefs = useMemo<ColDef[]>(
    () => [
      { field: 'name', headerName: 'Name', flex: 2 },
      {
        field: 'createdByEmployee',
        headerName: 'Created by',
        flex: 1,
        filter: 'agSetColumnFilter',
      },
      {
        field: 'questionCount',
        headerName: 'Questions',
        flex: 1,
      },
    ],
    []
  );

  const rowData = engagementTemplates?.data ?? [];

  const getKebabMenuOptions = useCallback(
    (data: object) => {
      if ('isArchived' in data && data.isArchived) {
        return [
          {
            label: 'Unarchive',
            onClick: () => {
              // type guard
              if ('id' in data && typeof data.id === 'number') handleArchiveTemplate(data.id, false);
            },
          },
        ];
      }

      return [
        {
          label: 'Duplicate',
          onClick: () => {
            if ('id' in data && typeof data.id === 'number') handleDuplicateTemplate(data.id);
          },
        },
        {
          label: 'Archive',
          onClick: () => {
            // type guard
            if ('id' in data && typeof data.id === 'number') handleArchiveTemplate(data.id, true);
          },
        },
      ];
    },
    [handleArchiveTemplate, handleDuplicateTemplate]
  );

  return (
    <Flex flexDir="column" p="12px" gap="12px">
      {/* Header */}
      <Flex justifyContent="space-between" p="4px 8px">
        <Box>
          <TextHeader4 bold>Templates</TextHeader4>
          <TextLabel>Design and view sample / archived templates for your engagement surveys.</TextLabel>
        </Box>
        <Button iconLeft={Add} variant="outline" style={{ height: '32px', whiteSpace: 'nowrap' }} onClick={onNewClick}>
          Add new
        </Button>
      </Flex>
      {/* Chips */}
      <Flex gap="8px">
        <Chip
          label={`Active${templateCounts.ACTIVE !== '' ? ` (${templateCounts.ACTIVE})` : ''}`}
          selected={selectedTemplateType === TEMPLATES_TYPE.ACTIVE}
          onSelectedChange={() => setSelectedTemplateType(TEMPLATES_TYPE.ACTIVE)}
        />
        <Chip
          label={`Archived${templateCounts.ARCHIVED !== '' ? ` (${templateCounts.ARCHIVED})` : ''}`}
          selected={selectedTemplateType === TEMPLATES_TYPE.ARCHIVED}
          onSelectedChange={() => setSelectedTemplateType(TEMPLATES_TYPE.ARCHIVED)}
        />
      </Flex>
      <Box w="100%">
        <AgGridTable
          ref={gridRef}
          defaultColDef={{
            resizable: false,
            autoHeight: true,
            lockVisible: true,
            headerComponentParams: {
              enableMenu: true,
              enableSorting: true,
            },
            menuTabs: ['filterMenuTab'],
            headerComponent: AgGridHeader,
            filter: 'agTextColumnFilter',
            cellStyle: { display: 'flex', alignItems: 'center', overflow: 'hidden' },
          }}
          autoGroupColumnDef={null}
          columnDefs={columnDefs}
          rowData={rowData}
          domLayout="autoHeight"
          containerAutoHeight
          pagination
          headerHeight={40}
          suppressPaginationPanel={!rowData?.length || rowData.length < 10}
          paginationPageSize={10}
          loading={isEngagementTemplatesLoading || isArchiveTemplateLoading || isDuplicateTemplateLoading}
          kebabMenuIsRowSpecific
          kebabMenuOptionsGenerator={getKebabMenuOptions}
          onCellClicked={({ data }) => handleOpenTemplate(data.id)}
        />
      </Box>
    </Flex>
  );
};
